/**
 * API client for user highlight endpoints.
 * Handles user-specific text highlights for chapters.
 */

import axios from 'axios';
import { getAuthToken, getPortalType } from '@/utils/auth';
import { API_BASE_URL } from './config';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * User Highlights API
 */
export const userHighlightsAPI = {
  /**
   * Get all highlights for a chapter for the current user
   * @param {number} chapterId - The chapter ID
   * @returns {Promise<Array>} Array of highlight objects
   */
  getChapterHighlights: async (chapterId) => {
    try {
      // Add cache-busting timestamp to prevent service worker/browser cache
      const timestamp = Date.now();
      const response = await axios.get(
          `${API_BASE_URL}/users/highlights/?chapter_id=${chapterId}&_t=${timestamp}`,
        {
          headers: {
            ...getAuthHeaders(),
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      // Handle both paginated and non-paginated responses
      // Paginated: { count, next, previous, results: [...] }
      // Non-paginated: { count, results: [...] }
      // Direct array: [...]
      let highlights = [];
      
      console.log('🔍 Raw response.data:', response.data);
      console.log('🔍 response.data type:', typeof response.data);
      console.log('🔍 response.data.results:', response.data?.results);
      console.log('🔍 response.data.results type:', typeof response.data?.results);
      console.log('🔍 response.data.results isArray:', Array.isArray(response.data?.results));
      
      if (Array.isArray(response.data)) {
        highlights = response.data;
        console.log('✅ Using direct array (response.data is array)');
      } else if (response.data && response.data.results !== undefined) {
        // Check if results exists (even if it's an empty array)
        if (Array.isArray(response.data.results)) {
          highlights = response.data.results;
          console.log('✅ Using results array, count:', highlights.length);
        } else {
          console.warn('⚠️ response.data.results exists but is not an array:', response.data.results);
          highlights = [];
        }
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        highlights = [];
      }
      
      console.log('🔍 Highlights API Response structure:', {
        isArray: Array.isArray(response.data),
        hasResults: !!response.data?.results,
        resultsIsArray: Array.isArray(response.data?.results),
        resultsLength: response.data?.results?.length,
        highlightsCount: highlights.length,
        rawDataKeys: response.data ? Object.keys(response.data) : [],
        rawDataCount: response.data?.count,
        rawDataResults: response.data?.results
      });
      
      return highlights;
    } catch (error) {
      console.error('Error fetching highlights:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  /**
   * Create a new highlight
   * @param {Object} highlightData - Highlight data
   * @param {number} highlightData.chapter_id - Chapter ID
   * @param {string} highlightData.text - Highlighted text
   * @param {number} [highlightData.page_number] - PDF page number
   * @param {string} [highlightData.section_name] - PDF section name or context
   * @param {string} [highlightData.color] - Highlight color (default: 'yellow')
   * @returns {Promise<Object>} Created highlight object
   */
  create: async (highlightData) => {
    try {
      const response = await axios.post(
          `${API_BASE_URL}/users/highlights/`,
        highlightData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating highlight:', error);
      throw error;
    }
  },

  /**
   * Update a highlight
   * @param {number} highlightId - Highlight ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated highlight object
   */
  update: async (highlightId, updates) => {
    try {
      const response = await axios.patch(
          `${API_BASE_URL}/users/highlights/${highlightId}/`,
        updates,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating highlight:', error);
      throw error;
    }
  },

  /**
   * Delete a highlight
   * @param {number} highlightId - Highlight ID
   * @returns {Promise<void>}
   */
  delete: async (highlightId) => {
    try {
      await axios.delete(
          `${API_BASE_URL}/users/highlights/${highlightId}/`,
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error('Error deleting highlight:', error);
      throw error;
    }
  }
};

